import useSWR, { preload } from 'swr';
import { useCallback } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { UserProfile } from 'src/types/common.types';
import { UserAccountState } from 'src/enums/shared.enums';
import { getProfileApi } from 'src/api';

const buildProfile = (raw: any): UserProfile => ({
  userId: raw.userId || '',
  email: raw.email || '',
  state: raw.state || UserAccountState.REGISTERED,
  accountType: raw.accountType || 'artist',
  displayName: raw.displayName || (raw.email ? raw.email.split('@')[0] : ''),
  firstName: raw.firstName || '',
  lastName: raw.lastName || '',
  bio: raw.bio || '',
  createdAt: raw.createdAt || new Date().toISOString(),
});

const fetcher = async () => {
  const response = await getProfileApi();
  return response?.profile ? buildProfile(response.profile) : null;
};

export const preloadProfile = () =>
  preload(['profile'], fetcher).catch(() => undefined);

export const useProfile = () => {
  // Reads Clerk's auth directly (not the useAuth shim) to avoid the
  // useAuth → useProfile → useAuth circular import. The shim is for
  // legacy consumers; this hook lives below it.
  const { isSignedIn } = useClerkAuth();

  const { data, error, isLoading, mutate } = useSWR<UserProfile | null>(
    isSignedIn ? ['profile'] : null,
    fetcher
  );

  // The previous Cognito implementation fell back to authProfile from
  // the auth-state context while SWR was loading. With Clerk we don't
  // have an auth-context profile; SWR's data is the only source.
  const profile = data ?? null;

  const refreshProfile = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const updateProfileCache = useCallback(async (updated: UserProfile) => {
    await mutate(updated, false);
  }, [mutate]);

  const clearProfileCache = useCallback(async () => {
    await mutate(null, false);
  }, [mutate]);

  return {
    profile,
    // Report loading only when SWR is fetching AND we have no usable data
    // (not even fallback from auth context). This prevents AuthGuard from
    // blocking when profile data is already available via the auth context.
    loading: isLoading && !profile,
    error,
    refreshProfile,
    updateProfileCache,
    clearProfileCache,
  };
};
